<template>
  <div class="referral-page">
    <h2>我的推荐券</h2>
    <p class="hint">
      创建可命名的推荐券分享给客户。客户绑定后，普通推荐券双方获得平台赠金；合伙人推荐券建立终身消费分润关系（需已开通合伙人）。
    </p>

    <el-card class="create-card" shadow="never">
      <el-form inline @submit.prevent="createCoupon">
        <el-form-item label="券名称">
          <el-input v-model="createForm.displayName" maxlength="20" placeholder="如：老王专属福利" style="width: 200px" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="createForm.couponType" style="width: 140px">
            <el-option label="普通推荐券" value="normal" />
            <el-option label="合伙人券" value="partner" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="creating" @click="createCoupon">创建推荐券</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <div class="toolbar">
      <el-select v-model="statusFilter" placeholder="状态" class="filter-select" clearable @change="fetchList">
        <el-option label="全部状态" value="" />
        <el-option label="可用" value="active" />
        <el-option label="已使用" value="used" />
      </el-select>
      <el-select v-model="typeFilter" placeholder="类型" class="filter-select" clearable @change="fetchList">
        <el-option label="全部类型" value="" />
        <el-option label="普通" value="normal" />
        <el-option label="合伙人" value="partner" />
      </el-select>
      <el-button @click="fetchList">刷新</el-button>
      <el-button link type="primary" @click="$router.push('/referral/earnings')">查看收益</el-button>
    </div>

    <el-table :data="coupons" v-loading="loading" style="width: 100%; margin-top: 16px">
      <el-table-column prop="displayName" label="名称" min-width="120" />
      <el-table-column prop="couponNo" label="券号" width="150">
        <template #default="scope">
          <span class="code-text">{{ scope.row.couponNo }}</span>
          <el-button link type="primary" size="small" @click="copyText(scope.row.couponNo)">复制</el-button>
        </template>
      </el-table-column>
      <el-table-column prop="couponType" label="类型" width="100">
        <template #default="scope">
          {{ scope.row.couponType === 'partner' ? '合伙人' : '普通' }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="90">
        <template #default="scope">
          <el-tag :type="scope.row.status === 'active' ? 'success' : 'info'">
            {{ statusLabel(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="绑定用户" min-width="120">
        <template #default="scope">
          <span v-if="scope.row.usedByUserId">{{ scope.row.usedByUsername || scope.row.usedByUserId }}</span>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170">
        <template #default="scope">{{ formatTime(scope.row.createdAt) }}</template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > pageSize"
      class="pager"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      @current-change="onPageChange"
    />
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ReferralCouponsView',
  data() {
    return {
      loading: false,
      creating: false,
      coupons: [],
      total: 0,
      page: 1,
      pageSize: 20,
      statusFilter: '',
      typeFilter: '',
      createForm: {
        displayName: '',
        couponType: 'normal'
      }
    }
  },
  created() {
    if (!localStorage.getItem('jwt_token')) {
      this.$goToLogin(this.$route.fullPath)
      return
    }
    this.fetchList()
  },
  methods: {
    statusLabel(s) {
      if (s === 'active') return '可用'
      if (s === 'used') return '已使用'
      return s || '—'
    },
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await axios.post('/api/v1/referral/coupon/my-list', {
          page: this.page,
          pageSize: this.pageSize,
          status: this.statusFilter,
          couponType: this.typeFilter
        })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '加载失败')
          return
        }
        this.coupons = res.data.data?.coupons || []
        this.total = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async createCoupon() {
      const name = (this.createForm.displayName || '').trim()
      if (!name) {
        this.$message.warning('请填写推荐券名称')
        return
      }
      this.creating = true
      try {
        const res = await axios.post('/api/v1/referral/coupon/create', {
          displayName: name,
          couponType: this.createForm.couponType
        })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '创建失败')
          return
        }
        this.$message.success('推荐券已创建')
        this.createForm.displayName = ''
        this.page = 1
        await this.fetchList()
      } catch (e) {
        this.$message.error(e.response?.data?.message || '创建失败')
      } finally {
        this.creating = false
      }
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制')
      }).catch(() => {
        this.$message.warning('复制失败：' + text)
      })
    },
    onPageChange(p) {
      this.page = p
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.referral-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px;
}
.hint { color: #606266; margin-bottom: 16px; }
.create-card { margin-bottom: 16px; }
.toolbar { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.filter-select { width: 130px; }
.code-text { font-family: monospace; font-weight: 600; margin-right: 8px; }
.pager { margin-top: 16px; justify-content: center; }
</style>
